import { useRouter } from 'next/router'

interface UseCampaignSlugProps {
  campaignSlug: string
  /**
   * useRouterなどで取得したpathを元に、campaignSlugを付与したURLを生成する
   * */
  gen: (path: `/${string}`) => string
  /**
   * 現在のパスがホームかどうか。
   * /{campaignSlug}/ のパスであればtrue、それ以外はfalse
   * */
  isHome: boolean
  LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: string
  LOCAL_STORAGE_I18N_KEY: string
  LOCAL_STORAGE_USERNAME_KEY: string
  LOCAL_STORAGE_JWT_KEY: string
  LOCAL_STORAGE_REFRESH_KEY: string
  LOCAL_STORAGE_PERIOD_KEY: string
  LOCAL_STORAGE_EXPIRATION_TIME_KEY: string
  LOCAL_STORAGE_CLIENT_ID_KEY: string
  LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: string
  LOCAL_STORAGE_QRCODE_KEY: string
  LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: string
  LOCAL_STORAGE_SPOT_SELECTION_KEY: string
  LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => string
}

/**
 * slugからcampaignを取得し、そのほかのlocalStorageなどのキーも取得する
 *  */
export const useCampaignSlug = (): UseCampaignSlugProps => {
  const router = useRouter()
  const { campaign } = router.query
  const path = router.asPath
  const altCampaign = path.split('/')[1]
  // /{campaignSlug}/ のパスであればhome以下とみなしtrue、それ以外はfalse
  const isHome = path.split('/').length - 1 <= 2
  const withoutBasePath = process.env.NEXT_PUBLIC_WITHOUT_BASE_PATH as '0' | '1'

  if (withoutBasePath === '0') {
    if (typeof campaign === 'string' && campaign !== '/[campaign]' && campaign !== '[campaign]') {
      return {
        campaignSlug: campaign,
        gen: (path: `/${string}`) => `/${campaign}${path}`,
        isHome,
        LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: `${campaign}_DEPLOY_TIMESTAMP`,
        LOCAL_STORAGE_I18N_KEY: `${campaign}_i18nextLng`,
        LOCAL_STORAGE_USERNAME_KEY: `${campaign}-username`,
        LOCAL_STORAGE_JWT_KEY: `${campaign}-jwt`,
        LOCAL_STORAGE_REFRESH_KEY: `${campaign}-refresh`,
        LOCAL_STORAGE_PERIOD_KEY: `${campaign}-period`,
        LOCAL_STORAGE_EXPIRATION_TIME_KEY: `${campaign}-expiration-time`,
        LOCAL_STORAGE_CLIENT_ID_KEY: `${campaign}-clientid`,
        LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: `${campaign}-register-Questionnaires`,
        LOCAL_STORAGE_QRCODE_KEY: `${campaign}-qrcode`,
        LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: `${campaign}-unauthorized-count`,
        LOCAL_STORAGE_SPOT_SELECTION_KEY: `${campaign}-spot-selection`,
        LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => `${campaign}-${hookPoint}-popup`,
      }
    } else if (path) {
      return {
        campaignSlug: altCampaign,
        gen: (path: `/${string}`) => `/${altCampaign}${path}`,
        isHome,
        LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: `${altCampaign}_DEPLOY_TIMESTAMP`,
        LOCAL_STORAGE_I18N_KEY: `${altCampaign}_i18nextLng`,
        LOCAL_STORAGE_USERNAME_KEY: `${altCampaign}-username`,
        LOCAL_STORAGE_JWT_KEY: `${altCampaign}-jwt`,
        LOCAL_STORAGE_REFRESH_KEY: `${altCampaign}-refresh`,
        LOCAL_STORAGE_PERIOD_KEY: `${altCampaign}-period`,
        LOCAL_STORAGE_EXPIRATION_TIME_KEY: `${altCampaign}-expiration-time`,
        LOCAL_STORAGE_CLIENT_ID_KEY: `${altCampaign}-clientid`,
        LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: `${altCampaign}-register-Questionnaires`,
        LOCAL_STORAGE_QRCODE_KEY: `${altCampaign}-qrcode`,
        LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: `${altCampaign}-unauthorized-count`,
        LOCAL_STORAGE_SPOT_SELECTION_KEY: `${altCampaign}-spot-selection`,
        LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => `${altCampaign}-${hookPoint}-popup`,
      }
    }
  } else if (withoutBasePath === '1') {
    // 現在、next.jsの`pages/[campaign]`のルーティングを採用している都合上、`/`で終わることができない
    const campaignFromEnv = process.env.NEXT_PUBLIC_CAMPAIGN_SLUG
    if (campaignFromEnv) {
      return {
        campaignSlug: campaignFromEnv,
        gen: (path: `/${string}`) => path,
        isHome: path.split('/').length - 1 <= 1,
        LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: `${campaignFromEnv}_DEPLOY_TIMESTAMP`,
        LOCAL_STORAGE_I18N_KEY: `${campaignFromEnv}_i18nextLng`,
        LOCAL_STORAGE_USERNAME_KEY: `${campaignFromEnv}-username`,
        LOCAL_STORAGE_JWT_KEY: `${campaignFromEnv}-jwt`,
        LOCAL_STORAGE_REFRESH_KEY: `${campaignFromEnv}-refresh`,
        LOCAL_STORAGE_PERIOD_KEY: `${campaignFromEnv}-period`,
        LOCAL_STORAGE_EXPIRATION_TIME_KEY: `${campaignFromEnv}-expiration-time`,
        LOCAL_STORAGE_CLIENT_ID_KEY: `${campaignFromEnv}-clientid`,
        LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: `${campaignFromEnv}-register-Questionnaires`,
        LOCAL_STORAGE_QRCODE_KEY: `${campaignFromEnv}-qrcode`,
        LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: `${campaignFromEnv}-unauthorized-count`,
        LOCAL_STORAGE_SPOT_SELECTION_KEY: `${campaignFromEnv}-spot-selection`,
        LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) => `${campaignFromEnv}-${hookPoint}-popup`,
      }
    }
  }

  return {
    campaignSlug: process.env.NEXT_PUBLIC_SERVICE_NAME,
    gen: (path: `/${string}`) => `/${process.env.NEXT_PUBLIC_SERVICE_NAME}${path}`,
    isHome,
    LOCAL_STORAGE_DEPLOY_TIMESTAMP_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}_DEPLOY_TIMESTAMP`,
    LOCAL_STORAGE_I18N_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}_i18nextLng`,
    LOCAL_STORAGE_USERNAME_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-username`,
    LOCAL_STORAGE_JWT_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-jwt`,
    LOCAL_STORAGE_REFRESH_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-refresh`,
    LOCAL_STORAGE_PERIOD_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-period`,
    LOCAL_STORAGE_EXPIRATION_TIME_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-expiration-time`,
    LOCAL_STORAGE_CLIENT_ID_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-clientid`,
    LOCAL_STORAGE_REGISTER_QUESTIONNAIRES_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-register-Questionnaires`,
    LOCAL_STORAGE_QRCODE_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-qrcode`,
    LOCAL_STORAGE_UNAUTHORIZED_COUNT_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-unauthorized-count`,
    LOCAL_STORAGE_SPOT_SELECTION_KEY: `${process.env.NEXT_PUBLIC_SERVICE_NAME}-spot-selection`,
    LOCAL_STORAGE_POPUP_HOOKPOINT_KEY: (hookPoint: string) =>
      `${process.env.NEXT_PUBLIC_SERVICE_NAME}-${hookPoint}-popup`,
  }
}
